import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, ElementType, ReactNode } from 'react';

type Direction = 'up' | 'down' | 'left' | 'right' | 'scale' | 'none';

interface RevealProps {
  children?: ReactNode;
  /** Element to render. Defaults to a div. Use to replace an existing wrapper without adding DOM depth. */
  as?: ElementType;
  className?: string;
  style?: CSSProperties;
  id?: string;
  /** Direction the content travels in from. */
  direction?: Direction;
  /** Delay before the transition starts, in ms — handy for staggering siblings. */
  delay?: number;
  /** Reveal only once (default). Set false to re-hide when scrolled out of view. */
  once?: boolean;
  /** Fraction of the element that must be visible before it reveals. */
  threshold?: number;
  /** Any other props (e.g. src/alt on an img) are forwarded to the rendered element. */
  [key: string]: unknown;
}

export function Reveal({
  children,
  as: Tag = 'div',
  className = '',
  style,
  id,
  direction = 'up',
  delay = 0,
  once = true,
  threshold = 0.15,
  ...rest
}: RevealProps) {
  const ref = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Fallback for environments without IntersectionObserver (e.g. SSR/old browsers).
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            if (once) observer.unobserve(entry.target);
          } else if (!once) {
            setVisible(false);
          }
        });
      },
      { threshold, rootMargin: '0px 0px -10% 0px' },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [once, threshold]);

  return (
    <Tag
      ref={ref}
      id={id}
      className={`ph-reveal ph-reveal-${direction}${visible ? ' is-visible' : ''}${
        className ? ` ${className}` : ''
      }`}
      style={delay ? { ...style, transitionDelay: `${delay}ms` } : style}
      {...rest}
    >
      {children}
    </Tag>
  );
}
